import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress,
  Paper, Stack, IconButton, Tooltip, Divider, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ScreenShare, StopScreenShare, Mouse, TouchApp,
  Refresh, FiberManualRecord, History,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { tokens } from '../theme';
import { deviceService } from '../services/api';

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'https://praxysrd.com/api')
  .replace('/api', '');

// ─── Estado de sesión ────────────────────────────────────────────────────────

const STATUS = {
  IDLE:              'idle',
  STARTING:          'starting',
  WAITING_TABLET:    'waiting_tablet',
  CONNECTED:         'connected',
  TABLET_DISCONNECTED: 'tablet_disconnected',
  ENDED:             'ended',
  ERROR:             'error',
};

const STATUS_LABEL = {
  [STATUS.IDLE]:               'Sin sesión',
  [STATUS.STARTING]:           'Iniciando…',
  [STATUS.WAITING_TABLET]:     'Esperando tablet…',
  [STATUS.CONNECTED]:          'Conectado',
  [STATUS.TABLET_DISCONNECTED]:'Tablet desconectada',
  [STATUS.ENDED]:              'Sesión finalizada',
  [STATUS.ERROR]:              'Error',
};

const STATUS_COLOR = {
  [STATUS.CONNECTED]:   'success',
  [STATUS.WAITING_TABLET]: 'warning',
  [STATUS.ERROR]:       'error',
  [STATUS.STARTING]:    'info',
};

// ─── Componente principal ────────────────────────────────────────────────────

const RemoteView = ({ devices = [] }) => {
  const canvasRef       = useRef(null);
  const socketRef       = useRef(null);
  const imgRef          = useRef(new window.Image());
  const sessionTokenRef = useRef(null);
  // Swipe tracking
  const pointerDown     = useRef(null);

  const [tab, setTab]               = useState(0);  // 0 = sesión activa, 1 = historial
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [status, setStatus]         = useState(STATUS.IDLE);
  const [fps, setFps]               = useState(0);
  const lastFpsRef                  = useRef({ count: 0, ts: Date.now() });

  // Historial
  const [history, setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ─── Dibujar frame en canvas ───────────────────────────────────────────────

  const drawFrame = useCallback(({ data, width, height }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Ajustar tamaño del canvas si cambió la resolución
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width  = width;
      canvas.height = height;
    }

    imgRef.current.onload = () => {
      canvas.getContext('2d').drawImage(imgRef.current, 0, 0);
    };
    imgRef.current.src = `data:image/jpeg;base64,${data}`;

    // FPS counter
    lastFpsRef.current.count++;
    const now = Date.now();
    if (now - lastFpsRef.current.ts >= 1000) {
      setFps(lastFpsRef.current.count);
      lastFpsRef.current = { count: 0, ts: now };
    }
  }, []);

  // ─── Enviar evento de control ──────────────────────────────────────────────

  const sendControl = useCallback((type, x, y, endX = 0, endY = 0) => {
    if (!socketRef.current || status !== STATUS.CONNECTED) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Normalizar coordenadas del canvas CSS → coordenadas reales de captura
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;

    socketRef.current.emit('admin:control', {
      sessionToken: sessionTokenRef.current,
      event: {
        type,
        x:    x    * scaleX,
        y:    y    * scaleY,
        endX: endX * scaleX,
        endY: endY * scaleY,
      },
    });
  }, [status]);

  // ─── Handlers de puntero (tap / swipe / longpress) ───────────────────────

  const handlePointerDown = useCallback((e) => {
    if (status !== STATUS.CONNECTED) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = canvasRef.current.getBoundingClientRect();
    pointerDown.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      ts: Date.now(),
    };
  }, [status]);

  const handlePointerUp = useCallback((e) => {
    if (!pointerDown.current || status !== STATUS.CONNECTED) return;
    const rect   = canvasRef.current.getBoundingClientRect();
    const upX    = e.clientX - rect.left;
    const upY    = e.clientY - rect.top;
    const { x: downX, y: downY, ts } = pointerDown.current;
    pointerDown.current = null;

    const dx       = upX - downX;
    const dy       = upY - downY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const duration = Date.now() - ts;

    if (distance < 12 && duration >= 500) {
      // Long press
      sendControl('longpress', downX, downY);
    } else if (distance < 12) {
      // Tap
      sendControl('tap', downX, downY);
    } else {
      // Swipe — enviar coordenadas inicio y fin
      sendControl('swipe', downX, downY, upX, upY);
    }
  }, [status, sendControl]);

  // ─── Gestión de sesión ────────────────────────────────────────────────────

  const startSession = useCallback(async (device) => {
    if (!device) return;
    setStatus(STATUS.STARTING);
    setSelectedDevice(device);

    try {
      const res = await deviceService.startRemoteSession(device.id);
      if (!res.success) throw new Error(res.error || 'Error desconocido');

      const token = res.sessionToken;
      sessionTokenRef.current = token;

      // Conectar Socket.io al namespace /remote
      const socket = io(`${SOCKET_URL}/remote`, {
        // polling primero: más compatible con hosting compartido (BanaHosting)
        // sube a websocket si está disponible
        transports: ['polling', 'websocket'],
        auth: { token: localStorage.getItem('authToken') },
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('admin:join', { sessionToken: token });
      });

      socket.on('session:waiting', () => {
        setStatus(STATUS.WAITING_TABLET);
      });

      socket.on('session:tablet_connected', () => {
        setStatus(STATUS.CONNECTED);
      });

      socket.on('frame', drawFrame);

      socket.on('session:tablet_disconnected', () => {
        setStatus(STATUS.TABLET_DISCONNECTED);
      });

      socket.on('session:ended', () => {
        setStatus(STATUS.ENDED);
        socket.disconnect();
      });

      socket.on('session:error', ({ message }) => {
        setStatus(STATUS.ERROR);
        console.error('Remote session error:', message);
        socket.disconnect();
      });

      socket.on('disconnect', () => {
        if (status === STATUS.CONNECTED) setStatus(STATUS.TABLET_DISCONNECTED);
      });

    } catch (err) {
      console.error('Error iniciando sesión:', err);
      setStatus(STATUS.ERROR);
    }
  }, [drawFrame, status]);

  const endSession = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('admin:end', { sessionToken: sessionTokenRef.current });
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    // Limpiar canvas
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setStatus(STATUS.IDLE);
    setFps(0);
    sessionTokenRef.current = null;
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await deviceService.getRemoteHistory(50);
      if (res.success) setHistory(res.sessions || []);
    } catch (e) {
      console.error('Error cargando historial:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 1) loadHistory();
  }, [tab, loadHistory]);

  // Cleanup al desmontar
  useEffect(() => () => { endSession(); }, [endSession]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const onlineDevices = devices.filter(d => d.status === 'active');
  const isSessionActive = [STATUS.WAITING_TABLET, STATUS.CONNECTED, STATUS.TABLET_DISCONNECTED].includes(status);

  // ─── Helpers de historial ────────────────────────────────────────────────
  const fmtDuration = (secs) => {
    if (!secs || secs < 0) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-DO') : '—';
  const statusChipColor = (s) => ({
    active: 'success', ended: 'default', error: 'error',
    pending: 'warning', connecting: 'info',
  }[s] || 'default');

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Header + Tabs */}
      <Box>
        <Typography variant="h5" fontWeight={700} color={tokens.color.ink.primary}>
          Soporte Remoto
        </Typography>
        <Typography variant="body2" color={tokens.color.ink.secondary} mt={0.5}>
          Visualización y control remoto de tablets — tráfico cifrado vía servidor
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mt: 1.5, borderBottom: `1px solid ${tokens.color.border.default}` }}
        >
          <Tab label="Sesión activa"   icon={<ScreenShare />}  iconPosition="start" />
          <Tab label="Historial"       icon={<History />}       iconPosition="start" />
        </Tabs>
      </Box>

      {/* ── Pestaña 0: Sesión activa ─────────────────────────────────────── */}
      {tab === 1 ? (

        /* ── Pestaña 1: Historial ──────────────────────────────────────── */
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button size="small" startIcon={<Refresh />} onClick={loadHistory}
              disabled={historyLoading} sx={{ textTransform: 'none' }}>
              Actualizar
            </Button>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined"
              sx={{ borderColor: tokens.color.border.default }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: tokens.color.surface.subtle }}>
                  <TableRow>
                    {['Dispositivo','Admin','Inicio','Duración','Estado'].map(h => (
                      <TableCell key={h}
                        sx={{ fontWeight: 600, color: tokens.color.ink.secondary, fontSize: 12 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center"
                        sx={{ py: 4, color: tokens.color.ink.muted }}>
                        Sin sesiones registradas
                      </TableCell>
                    </TableRow>
                  ) : history.map(row => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.device_name || '—'}
                        </Typography>
                        <Typography variant="caption" color={tokens.color.ink.muted}
                          sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {row.device_identifier?.slice(0, 18)}…
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: tokens.color.ink.secondary }}>
                        {row.admin_username || '—'}
                      </TableCell>
                      <TableCell sx={{ color: tokens.color.ink.secondary, fontSize: 12 }}>
                        {fmtDate(row.created_at)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                        {fmtDuration(row.duration_seconds)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={statusChipColor(row.status)}
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

      ) : (
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>

        {/* Panel izquierdo: lista de dispositivos */}
        <Paper
          variant="outlined"
          sx={{
            width: 260,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderColor: tokens.color.border.default,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.color.border.default}` }}>
            <Typography variant="subtitle2" fontWeight={600} color={tokens.color.ink.primary}>
              Dispositivos en línea
            </Typography>
            <Typography variant="caption" color={tokens.color.ink.muted}>
              {onlineDevices.length} disponible{onlineDevices.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {onlineDevices.length === 0 ? (
              <Typography variant="caption" color={tokens.color.ink.muted} sx={{ p: 2, display: 'block' }}>
                No hay dispositivos en línea
              </Typography>
            ) : (
              onlineDevices.map(device => (
                <Box
                  key={device.id}
                  onClick={() => !isSessionActive && startSession(device)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    cursor: isSessionActive ? 'default' : 'pointer',
                    backgroundColor: selectedDevice?.id === device.id && isSessionActive
                      ? tokens.color.brand[50]
                      : 'transparent',
                    border: selectedDevice?.id === device.id && isSessionActive
                      ? `1px solid ${tokens.color.brand[200]}`
                      : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isSessionActive ? undefined : tokens.color.surface.overlay,
                    },
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={500} color={tokens.color.ink.primary} noWrap>
                    {device.device_name}
                  </Typography>
                  <Typography variant="caption" color={tokens.color.ink.muted} noWrap
                    sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {device.device_id?.slice(0, 20)}…
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Paper>

        {/* Panel derecho: visor */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>

          {/* Barra de estado */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderColor: tokens.color.border.default,
            }}
          >
            <Chip
              size="small"
              icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
              label={STATUS_LABEL[status]}
              color={STATUS_COLOR[status] || 'default'}
              variant="outlined"
            />

            {status === STATUS.CONNECTED && (
              <Typography variant="caption" color={tokens.color.ink.muted} sx={{ fontFamily: 'monospace' }}>
                {fps} fps
              </Typography>
            )}

            {selectedDevice && isSessionActive && (
              <Typography variant="caption" color={tokens.color.ink.secondary} sx={{ flex: 1 }} noWrap>
                {selectedDevice.device_name}
              </Typography>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              {status === STATUS.TABLET_DISCONNECTED && (
                <Tooltip title="Reconectar">
                  <IconButton size="small" onClick={() => startSession(selectedDevice)}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isSessionActive && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<StopScreenShare />}
                  onClick={endSession}
                  sx={{ textTransform: 'none' }}
                >
                  Terminar sesión
                </Button>
              )}
            </Box>
          </Paper>

          {/* Canvas de visualización */}
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderColor: tokens.color.border.default,
              backgroundColor: '#0a0a0a',
              overflow: 'hidden',
              position: 'relative',
              minHeight: 400,
            }}
          >
            {/* Canvas siempre montado para no perder la ref */}
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                cursor: status === STATUS.CONNECTED ? 'crosshair' : 'default',
                display: status === STATUS.CONNECTED ? 'block' : 'none',
                touchAction: 'none',   // evitar scroll del browser al swipear
                userSelect: 'none',
              }}
            />

            {/* Overlay de estado cuando no hay stream */}
            {status !== STATUS.CONNECTED && (
              <Stack alignItems="center" spacing={2} sx={{ color: '#666' }}>
                {status === STATUS.IDLE && (
                  <>
                    <ScreenShare sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography variant="body2">
                      Selecciona un dispositivo en línea para iniciar la sesión
                    </Typography>
                  </>
                )}
                {[STATUS.STARTING, STATUS.WAITING_TABLET].includes(status) && (
                  <>
                    <CircularProgress size={40} sx={{ color: tokens.color.brand[400] }} />
                    <Typography variant="body2">
                      {STATUS_LABEL[status]}
                    </Typography>
                  </>
                )}
                {status === STATUS.TABLET_DISCONNECTED && (
                  <>
                    <StopScreenShare sx={{ fontSize: 48, color: '#f59e0b', opacity: 0.6 }} />
                    <Typography variant="body2" color="#f59e0b">
                      La tablet se desconectó
                    </Typography>
                  </>
                )}
                {[STATUS.ENDED, STATUS.ERROR].includes(status) && (
                  <>
                    <StopScreenShare sx={{ fontSize: 48, opacity: 0.4 }} />
                    <Typography variant="body2">
                      {STATUS_LABEL[status]}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setStatus(STATUS.IDLE)}
                      sx={{ borderColor: '#555', color: '#aaa', textTransform: 'none' }}
                    >
                      Nueva sesión
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Paper>

          {/* Leyenda de controles */}
          {status === STATUS.CONNECTED && (
            <Stack direction="row" spacing={2} sx={{ px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Mouse sx={{ fontSize: 14, color: tokens.color.ink.muted }} />
                <Typography variant="caption" color={tokens.color.ink.muted}>
                  Clic → tap
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TouchApp sx={{ fontSize: 14, color: tokens.color.ink.muted }} />
                <Typography variant="caption" color={tokens.color.ink.muted}>
                  Arrastrar → swipe · Mantener 500ms → long press
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
      )}   {/* fin ternario tab */}
    </Box>
  );
};

export default RemoteView;
